<template lang="html">
    <div>
        <pay-in-details-modal
            v-if="isDetailsModalOpen"
            :payment="payment"
            :payments="reservation.payments"
            :reservation="reservation"
            @edit="openEditPayIn"
            @deleted="closeDetailsModal"
            @saved="closeDetailsModal"
            @close="isDetailsModalOpen = false"
        />

        <reservation-pay-in
            v-if="isEditPayInOpen"
            :editPayment="editTarget || payment"
            @close="isEditPayInOpen = false"
        />

        <table class="pay-in-detail-table w-100">
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Channel</th>
                    <th>Type</th>
                    <th>Method</th>
                    <th>Amount</th>
                    <th>Invoice</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td class="d-flex">
                        <div>{{ paymentDay(payment) }}</div>
                        <div class="pay-in-detail-table__time ml-1">{{ paymentTime(payment) }}</div>
                    </td>
                    <td>{{ paymentChannel(payment) }}</td>
                    <td>{{ paymentType(payment) }}</td>
                    <td>{{ selectedPaymentMethod || '-' }}</td>
                    <td class="pay-in-detail-table__amount">{{ paymentValue(payment) }}</td>
                    <td>{{ getInvoiceDisplay(payment) }}</td>
                    <td class="d-flex justify-content-end">
                        <button class="btn blue pay-in-detail-table__details-btn mr-1" @click="isDetailsModalOpen = true">Details</button>
                        <button class="btn action pay-in-detail-table__details-btn" :disabled="isFullyRefunded || !payment.canBeRefunded" @click="openRefund()">Refund</button>
                    </td>
                </tr>

            </tbody>
        </table>

        <div v-if="paymentNotes(payment)" class="mt-2">
            <span class="bold">Notes:</span> {{ paymentNotes(payment) }}
        </div>

        <div v-if="associatedRefunds.length > 0" class="associated-refunds-section">
            <div class="refunds-header bold mb-3">
                Associated Refunds ({{ associatedRefunds.length }})
            </div>
            
            <div v-for="refund in associatedRefunds" :key="refund.id" class="refund-item mb-3">
                <refund-area
                    :refund="refund"
                    :payment="payment"
                    :reservation="reservation"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import { reservationsMixin } from '@/mixins/reservations'
import PAYMENT_DIRECTION from '@/enums/paymentDirection'
import PayInDetailsModal from '@/tools/modal/ReservationEditV2/TabPayments/PayInDetailsModal'
import ReservationPayIn from '@/tools/modal/ReservationPayIn'
import RefundArea from '@/tools/modal/ReservationEditV2/TabPayments/expandedArea/refund'

export default {
    mixins: [
        reservationsMixin,
    ],
    props: {
        payment: {
            type: Object,
            default: null,
            required: true
        },
        reservation: {
            type: Object
        },
        isFullyRefunded: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            isDetailsModalOpen: false,
            isEditPayInOpen: false,
            editTarget: null,
        }
    },
    computed: {
        associatedRefunds() {
            if (!this.payment || !this.payment.refunds) {
                return []
            }
            return this.payment.refunds;
        },
        selectedPaymentMethod() {
            const allocations = this.payment.allocations || []
            if (allocations.length === 0) {
                return this.payment.paymentTypeDescription || null
            }
            const distinctMethods = [...new Set(allocations.map(allocation => allocation.paymentTypeDescription))]
            if (distinctMethods.length === 1) {
                return distinctMethods[0] || null
            }
            return 'Multiple'
        },
    },
    methods: {
        paymentDay: function (payment) {
            return payment.createdOn ? moment(payment.createdOn).format('DD MMM YYYY') : '-'
        },
        paymentTime: function (payment) {
            return payment.createdOn ? moment(payment.createdOn).format('HH:mm') : ''
        },
        paymentChannel: function (payment) {
            const allocations = payment.allocations || []
            if (allocations.length > 0) {
                const allAgent = allocations.every(allocation => allocation.isAgentPayment)
                if (allAgent) return 'Agent'
                const noneAgent = allocations.every(allocation => !allocation.isAgentPayment)
                if (noneAgent) return 'My Team'
                return 'Multiple'
            }
            if (payment.treasurerChannel && payment.treasurerChannel.name) return payment.treasurerChannel.name
            return '- Not found -'
        },
        paymentValue: function (payment) {
            return this.formatPrice(payment.paidValue) + payment.currency
        },
        paymentType: function (payment) {
            return payment.paymentDirection === PAYMENT_DIRECTION.PAY_IN ? 'Pay In' : 'Pay Out'
        },
        paymentNotes: function (payment) {
            return payment.description ? payment.description : null
        },
        getInvoiceDisplay: function (payment) {
            if (!payment.invoices || payment.invoices.length === 0) {
                return '-'
            }
            if (payment.invoices.length === 1) {
                const invoice = payment.invoices[0]
                return `${invoice.invoicE_NumeroDocumento}/${invoice.invoicE_Serie}`
            }
            return 'Multiple'
        },
        ...mapActions(['openModal', 'setRefundPaymentId', 'setRefundTransactionCode']),
        openEditPayIn(targetPayment) {
            this.editTarget = targetPayment && targetPayment.id ? targetPayment : null
            this.isDetailsModalOpen = false
            this.isEditPayInOpen = true
        },
        closeDetailsModal() {
            this.isDetailsModalOpen = false
        },
        // When the payment is a grouped transaction (more than one payment sharing the
        // same transaction code) the refund is scoped by transaction code; otherwise it
        // is scoped by a single payment id. The two are mutually exclusive.
        isMultiPaymentTransaction() {
            return !!(this.payment.isGrouped && this.payment.transactionCode)
        },
        scopeRefund(paymentId) {
            if (this.isMultiPaymentTransaction()) {
                this.setRefundTransactionCode(this.payment.transactionCode)
                this.setRefundPaymentId(null)
            } else {
                this.setRefundTransactionCode(null)
                this.setRefundPaymentId(paymentId)
            }
        },
        openRefund() {
            this.scopeRefund(this.payment.id)
            this.openModal('ReservationRefundTransaction')
        },
    },
    components: {
        PayInDetailsModal,
        ReservationPayIn,
        RefundArea,
    }
}
</script>

<style lang="scss" scoped>
    @import '~@/assets/scss/_variables.scss';

    .pay-in-detail-table {
        border-collapse: collapse;

        th {
            font-size: 0.68rem !important;
            font-weight: 600;
            text-transform: uppercase;
            color: #8a9ab0;
            padding: 0.4rem 0.5rem;
            border-bottom: 0.125rem solid #e0e7ef;
            text-align: left;
        }

        td {
            font-size: 0.75rem;
            font-weight: 600;
            padding: 0.4rem 0.5rem;
            vertical-align: top;
        }

        &__amount {
            color: $green;
        }

        &__time {
            font-size: 0.75rem;
            color: #8a9ab0;
        }

        &__details-btn {
            height: 1.375rem;
            line-height: 1.25rem;
            padding: 0 0.3rem;
            font-size: 0.65rem;
        }
    }

    .associated-refunds-section {
        background-color: #f8f9fa;
        border-radius: 0.25rem;
        padding: 1rem;
    }

    .refunds-header {
        font-size: 0.68rem;
        font-weight: 600;
        text-transform: uppercase;
        color: #8a9ab0;
        letter-spacing: 0.5px;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid #e0e7ef;
        display: flex;
        align-items: center;
    }

    .refund-item {
        background-color: white;
        border-radius: 0.25rem;
        padding: 1rem;
        border-left: 3px solid #ffc107;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

        &:hover {
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
        }
    }
</style>


// WEBPACK FOOTER //
// payIn.vue?475e2a20